import {
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  getAuth,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from 'firebase/auth'
import { FirebaseError } from 'firebase/app'
import type { AuthErrorFunction, LoginMethod } from './types'

export const ERROR_KEYS = {
  EMAIL_ALREADY_IN_USE: 'errors.emailAlreadyInUse',
  INVALID_EMAIL: 'errors.invalidEmail',
  OPERATION_NOT_ALLOWED: 'errors.operationNotAllowed',
  WEAK_PASSWORD: 'errors.weakPassword',
  USER_DISABLED: 'errors.userDisabled',
  USER_NOT_FOUND: 'errors.userNotFound',
  WRONG_PASSWORD: 'errors.wrongPassword',
  UNEXPECTED_ERROR: 'errors.unexpectedError',
  SIGN_OUT_ERROR: 'errors.signOutError',
  GOOGLE_SIGN_IN_FAILED: 'errors.googleSignInFailed',
  APPLE_SIGN_IN_FAILED: 'errors.appleSignInFailed',
  PLAY_SERVICES_NOT_AVAILABLE: 'errors.playServicesNotAvailable',
} as const

export const defaultAuthErrorFunction: AuthErrorFunction = async (
  operation,
) => {
  try {
    const result = await operation()
    return { success: true, user: result.user }
  } catch (err) {
    let errorMessage = 'errors.unexpectedError'
    if (err instanceof FirebaseError) {
      const errorCode = err.code
      if (errorCode) {
        switch (errorCode) {
          case 'auth/email-already-in-use': {
            errorMessage = ERROR_KEYS.EMAIL_ALREADY_IN_USE
            break
          }
          case 'auth/invalid-email': {
            errorMessage = ERROR_KEYS.INVALID_EMAIL
            break
          }
          case 'auth/operation-not-allowed': {
            errorMessage = ERROR_KEYS.OPERATION_NOT_ALLOWED
            break
          }
          case 'auth/weak-password': {
            errorMessage = ERROR_KEYS.WEAK_PASSWORD
            break
          }
          case 'auth/user-disabled': {
            errorMessage = ERROR_KEYS.USER_DISABLED
            break
          }
          case 'auth/user-not-found': {
            errorMessage = ERROR_KEYS.USER_NOT_FOUND
            break
          }
          case 'auth/wrong-password': {
            errorMessage = ERROR_KEYS.WRONG_PASSWORD
            break
          }
          default: {
            errorMessage = ERROR_KEYS.UNEXPECTED_ERROR
          }
        }
      }
    }
    return { success: false, error: errorMessage }
  }
}

export const defaultLoginMethods: LoginMethod = {
  registerWithEmail: async ({ email, name, password }) => {
    const credential = await createUserWithEmailAndPassword(
      getAuth(),
      email,
      password,
    )
    await updateProfile(credential.user, { displayName: name })
    await sendEmailVerification(credential.user)
    return credential
  },
  loginWithEmail: ({ email, password }) =>
    signInWithEmailAndPassword(getAuth(), email, password),
  loginWithApple: async (locale?: string) => {
    const authInstance = getAuth()
    const provider = new OAuthProvider('apple.com')
    provider.addScope('email')
    provider.addScope('name')
    provider.setCustomParameters({
      locale: locale ?? 'en',
    })
    return await signInWithPopup(authInstance, provider)
  },
  loginWithGoogle: async (locale?: string) => {
    const authInstance = getAuth()
    const provider = new GoogleAuthProvider()
    authInstance.languageCode = locale ?? 'en'
    return await signInWithPopup(authInstance, provider)
  },
  logout: () => signOut(getAuth()),
}
